import { useNavigate } from "react-router-dom";
import { HeartbeatIcon } from "./ui-icons";
import { Pill, HandHeart, ShieldCheck, Camera, Bell, Users } from "@phosphor-icons/react";

export default function Landing() {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#FDFBF7] text-[#1A2520]">
      {/* Nav */}
      <header className="max-w-7xl mx-auto px-6 py-5 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#2C5545] flex items-center justify-center">
            <Pill weight="duotone" className="text-white" size={22} />
          </div>
          <div className="font-heading font-extrabold text-xl tracking-tight">EverCara</div>
        </div>
        <div className="flex items-center gap-3">
          <button
            data-testid="landing-login-btn"
            onClick={() => navigate("/login")}
            className="text-[#1A2520] font-semibold text-sm hover:text-[#2C5545]"
          >
            Sign in
          </button>
          <button
            data-testid="landing-register-btn"
            onClick={() => navigate("/register")}
            className="bg-[#2C5545] hover:bg-[#224235] text-white rounded-full px-5 py-2.5 text-sm font-semibold transition-colors"
          >
            Get started
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="max-w-7xl mx-auto px-6 pt-8 pb-16 grid lg:grid-cols-2 gap-12 items-center">
        <div>
          <div className="inline-flex items-center gap-2 rounded-full border border-[#EBE8E0] bg-white px-4 py-1.5 text-xs uppercase tracking-widest text-[#576A62]">
            <HandHeart size={14} weight="duotone" className="text-[#2C5545]" />
            For patients & caregivers
          </div>
          <h1 className="mt-6 font-heading font-extrabold text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight">
            Care, kept <span className="text-[#2C5545]">gently</span> in order.
          </h1>
          <p className="mt-6 text-lg text-[#576A62] leading-relaxed max-w-xl">
            One warm, calm place to track doctors, appointments, prescriptions,
            refills, pharmacies, questions and insurance for the people you love —
            with easy-to-read medicine labels and AI pill identification.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => navigate("/register")}
              data-testid="hero-cta-btn"
              className="bg-[#2C5545] hover:bg-[#224235] text-white rounded-full px-7 py-4 font-semibold transition-colors"
            >
              Start caring for free
            </button>
            <button
              onClick={() => navigate("/login")}
              data-testid="hero-login-btn"
              className="border border-[#2C5545] text-[#2C5545] hover:bg-[#EBE8E0] rounded-full px-7 py-4 font-semibold transition-colors"
            >
              I already have an account
            </button>
          </div>
          <div className="mt-8 flex items-center gap-6 text-sm text-[#576A62]">
            <div className="flex items-center gap-2"><ShieldCheck size={18} weight="duotone" /> Private & secure</div>
            <div className="flex items-center gap-2"><Users size={18} weight="duotone" /> Multi-patient</div>
          </div>
        </div>
        <div className="relative">
          <div className="absolute -inset-6 bg-[#EBE8E0]/60 rounded-[2.5rem] blur-2xl -z-10" />
          <img
            src="https://images.pexels.com/photos/18509794/pexels-photo-18509794.jpeg"
            alt="Caregiver and senior woman"
            className="rounded-[2rem] shadow-lg object-cover w-full h-[520px]"
          />
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-6 py-16 border-t border-[#EBE8E0]">
        <div className="text-center max-w-2xl mx-auto">
          <div className="text-xs uppercase tracking-[0.2em] text-[#576A62] font-semibold">Everything in one place</div>
          <h2 className="mt-3 font-heading text-3xl sm:text-4xl font-bold">Built for the loving juggle of caregiving</h2>
        </div>
        <div className="mt-12 grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {[
            [Pill, "Meds & refills", "Track prescriptions, OTC meds, dosages and refill counts. Never miss a pickup."],
            [Camera, "AI pill scanner", "Snap a photo of a mystery pill — get color, shape, imprint & likely candidates."],
            [Bell, "Reminders", "Gentle in-app reminders for meds, appointments, refills and follow-ups."],
            [HandHeart, "Questions to ask", "Save questions per appointment so you never leave the visit wishing you'd asked."],
            [ShieldCheck, "Insurance cards", "Upload front & back of insurance cards. Always at your fingertips."],
            [Users, "Multi-patient", "Care for mom, dad and yourself from a single, easy dashboard."],
          ].map(([Icon, title, desc], i) => (
            <div key={i} className="bg-white border border-[#EBE8E0] rounded-2xl p-6 hover:shadow-md transition-shadow">
              <div className="w-11 h-11 rounded-full bg-[#EBE8E0] flex items-center justify-center">
                <Icon size={22} weight="duotone" className="text-[#2C5545]" />
              </div>
              <div className="mt-4 font-heading font-bold text-xl">{title}</div>
              <div className="mt-2 text-[#576A62] leading-relaxed">{desc}</div>
            </div>
          ))}
        </div>
      </section>

      <footer className="border-t border-[#EBE8E0] py-8 text-center text-sm text-[#576A62]">
        EverCara — made with care.
      </footer>
    </div>
  );
}
